import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Pencil, Plus, Star, Trash2 } from "lucide-react";

import { academicSetupService, type AcademicTerm, type AcademicYear } from "../../services/academicSetupService";
import { getAll } from "../../services/pagination";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { describeUsage, humanise, type RegisterConfig } from "../register/registerLogic";
import {
  TERM_LABELS,
  calendarChecklist,
  termConfig,
  termLabel,
  termPhase,
  weeksLabel,
  withTermCounts,
  yearConfig,
  type YearRow,
} from "./academicsLogic";
import { Notice, deleteErrorText, type NoticeState } from "../register/RecordParts";

type PlannerEvent = { id: number; name: string; event_type: string; start_date: string; end_date: string; status: string };

const plannerConfig: RegisterConfig<PlannerEvent> = {
  search: (row) => row.name,
  views: [
    { key: "all", label: "All" },
    { key: "published", label: "Published", match: (row) => row.status === "published" },
    { key: "draft", label: "Not published", match: (row) => row.status !== "published" },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  sorts: [
    { key: "start", label: "Soonest first", compare: (a, b) => a.start_date.localeCompare(b.start_date) },
    { key: "-start", label: "Latest first", compare: (a, b) => b.start_date.localeCompare(a.start_date) },
  ],
};

const today = () => new Date().toISOString().slice(0, 10);
const currentDot = (current: boolean, label: string) =>
  current ? <StatusDot label={label} dot="bg-emerald-500" /> : <span className="text-gray-300">—</span>;

/**
 * The school calendar: academic years, their three terms, and the planner of
 * published timetables. Years are leadership's; terms are also the director
 * of studies' -- the same split the server applies.
 */
export default function CalendarPanel({ canWriteYears, canWriteTerms, canDeleteTerms }: {
  canWriteYears: boolean;
  canWriteTerms: boolean;
  canDeleteTerms: boolean;
}) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [editingYear, setEditingYear] = useState<{ record?: AcademicYear } | null>(null);
  const [editingTerm, setEditingTerm] = useState<{ record?: AcademicTerm; year?: number } | null>(null);
  const [viewingYear, setViewingYear] = useState<number | null>(null);
  const [viewingTerm, setViewingTerm] = useState<number | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const years = useQuery({ queryKey: ["school-calendar-years"], queryFn: academicSetupService.getYears });
  const terms = useQuery({ queryKey: ["school-calendar-terms"], queryFn: academicSetupService.getTerms });
  const planner = useQuery({
    queryKey: ["academic-year-planner"],
    queryFn: async () => (await getAll<PlannerEvent>("/events/events/")).filter((item) => item.event_type === "academic"),
  });
  const termUsage = useQuery({
    queryKey: ["school-calendar-term", viewingTerm],
    queryFn: () => academicSetupService.getTerm(viewingTerm!),
    enabled: viewingTerm !== null && canDeleteTerms,
  });
  const refresh = () =>
    Promise.all(["school-calendar-years", "school-calendar-terms", "school-calendar-term"].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const saveYear = useMutation({
    mutationFn: ({ id, ...data }: Omit<AcademicYear, "id"> & { id?: number }) =>
      id ? academicSetupService.updateYear(id, data) : academicSetupService.createYear(data),
    onSuccess: (_result, variables) => {
      setEditingYear(null);
      setNotice({ tone: "success", text: variables.id ? "Academic year saved." : "Academic year created. Add its three terms next." });
      refresh();
    },
  });
  const saveTerm = useMutation({
    mutationFn: ({ id, ...data }: Omit<AcademicTerm, "id" | "academic_year_name"> & { id?: number }) =>
      id ? academicSetupService.updateTerm(id, data) : academicSetupService.createTerm(data),
    onSuccess: (_result, variables) => {
      setEditingTerm(null);
      setNotice({ tone: "success", text: variables.id ? "Term saved." : "Term added." });
      refresh();
    },
  });
  const makeCurrentYear = useMutation({
    mutationFn: (id: number) => academicSetupService.updateYear(id, { is_current: true }),
    onSuccess: () => {
      setNotice({ tone: "success", text: "Current academic year changed. Mark its term in progress as current too." });
      refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The year could not be made current.") }),
  });
  const makeCurrentTerm = useMutation({
    mutationFn: (id: number) => academicSetupService.updateTerm(id, { is_current: true }),
    onSuccess: () => {
      setNotice({ tone: "success", text: "Current term changed." });
      refresh();
    },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The term could not be made current.") }),
  });
  const deleteTerm = useMutation({
    mutationFn: academicSetupService.deleteTerm,
    onSuccess: () => {
      setViewingTerm(null);
      setConfirmingDelete(false);
      setNotice({ tone: "success", text: "Term deleted." });
      refresh();
    },
    onError: (error) => {
      setConfirmingDelete(false);
      setNotice({ tone: "error", text: deleteErrorText(error) });
    },
  });

  const yearRows = withTermCounts(years.data, terms.data);
  const checklist = calendarChecklist(years.data ?? [], terms.data ?? []);
  const setupDone = checklist.every((step) => step.done);
  const now = today();

  const yearColumns: RegisterColumn<YearRow>[] = [
    { header: "Academic year", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Starts", cell: (row) => row.start_date, exportValue: (row) => row.start_date, className: "tabular-nums text-gray-700" },
    { header: "Ends", cell: (row) => row.end_date, exportValue: (row) => row.end_date, className: "tabular-nums text-gray-700" },
    { header: "Length", cell: (row) => weeksLabel(row.start_date, row.end_date), exportValue: (row) => weeksLabel(row.start_date, row.end_date), className: "text-gray-700" },
    {
      header: "Terms",
      cell: (row) => <span className={row.termCount < 3 ? "text-amber-700" : undefined}>{row.termCount} of 3</span>,
      exportValue: (row) => row.termCount,
      align: "right",
    },
    { header: "Current", cell: (row) => currentDot(row.is_current, "Current year"), exportValue: (row) => (row.is_current ? "Yes" : "") },
  ];
  const termColumns: RegisterColumn<AcademicTerm>[] = [
    { header: "Term", cell: (row) => <span className="font-medium text-gray-900">{termLabel(row)}</span>, exportValue: termLabel },
    { header: "Academic year", cell: (row) => row.academic_year_name, exportValue: (row) => row.academic_year_name, className: "text-gray-700" },
    { header: "Starts", cell: (row) => row.start_date, exportValue: (row) => row.start_date, className: "tabular-nums text-gray-700" },
    { header: "Ends", cell: (row) => row.end_date, exportValue: (row) => row.end_date, className: "tabular-nums text-gray-700" },
    {
      header: "Mid-term break",
      cell: (row) => (row.mid_term_break_start ? `${row.mid_term_break_start} → ${row.mid_term_break_end}` : <span className="text-gray-300">None</span>),
      exportValue: (row) => (row.mid_term_break_start ? `${row.mid_term_break_start} to ${row.mid_term_break_end}` : ""),
      className: "whitespace-nowrap tabular-nums text-gray-700",
    },
    { header: "Length", cell: (row) => weeksLabel(row.start_date, row.end_date), exportValue: (row) => weeksLabel(row.start_date, row.end_date), className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...termPhase(row, now)} />, exportValue: (row) => termPhase(row, now).label },
  ];
  const plannerColumns: RegisterColumn<PlannerEvent>[] = [
    { header: "Entry", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Starts", cell: (row) => row.start_date, exportValue: (row) => row.start_date, className: "tabular-nums text-gray-700" },
    { header: "Ends", cell: (row) => row.end_date, exportValue: (row) => row.end_date, className: "tabular-nums text-gray-700" },
    {
      header: "Status",
      cell: (row) => <StatusDot label={humanise(row.status)} dot={row.status === "published" ? "bg-emerald-500" : "bg-amber-500"} />,
      exportValue: (row) => humanise(row.status),
    },
  ];

  const viewedYear = viewingYear !== null && !editingYear ? yearRows?.find((year) => year.id === viewingYear) : undefined;
  const viewedTerm = viewingTerm !== null && !editingTerm ? terms.data?.find((term) => term.id === viewingTerm) : undefined;
  const termInUse = termUsage.data?.in_use;
  const termDeletable = canDeleteTerms && termInUse !== undefined && !describeUsage(termInUse);

  return (
    <div className="space-y-6">
      {!years.isLoading && !terms.isLoading && (
        <section className={`rounded-lg border p-4 ${setupDone ? "border-gray-200 bg-white" : "border-primary-200 bg-primary-50"}`}>
          <h2 className="text-sm font-semibold text-gray-900">{setupDone ? "The calendar is set up" : "Set up the calendar in this order"}</h2>
          <ol className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {checklist.map((step, index) => (
              <li key={step.label} className="flex items-start gap-2 text-sm">
                {step.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}
                <span className={step.done ? "text-gray-500" : "text-gray-900"}>
                  {index + 1}. {step.label}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      <ClientRegister
        title="Academic years"
        description="Open a year to edit its dates, make it current or see its terms."
        actions={canWriteYears && (
          <button type="button" onClick={() => { saveYear.reset(); setEditingYear({}); }} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New academic year
          </button>
        )}
        rows={yearRows}
        config={yearConfig}
        columns={yearColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewingYear(row.id)}
        noun={["academic year", "academic years"]}
        exportName={{ file: "academic-years", title: "Academic Years" }}
        isLoading={years.isLoading || terms.isLoading}
        isError={years.isError}
        onRetry={() => years.refetch()}
        emptyText={canWriteYears ? "No academic years yet. Create the first one." : "No academic years have been set up yet."}
      />

      <ClientRegister
        title="Terms"
        description="Three terms to a year. Only the term in progress is marked current; fees, reports and attendance read it."
        actions={canWriteTerms && (
          <button
            type="button"
            disabled={!years.data?.length}
            title={years.data?.length ? undefined : "Create an academic year first"}
            onClick={() => { saveTerm.reset(); setEditingTerm({}); }}
            className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New term
          </button>
        )}
        rows={terms.data}
        config={termConfig(now)}
        columns={termColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => { setConfirmingDelete(false); setViewingTerm(row.id); }}
        noun={["term", "terms"]}
        exportName={{ file: "terms", title: "Terms" }}
        isLoading={terms.isLoading}
        isError={terms.isError}
        onRetry={() => terms.refetch()}
        emptyText="No terms yet. Add Term 1, Term 2 and Term 3 once the year exists."
      />

      <ClientRegister
        title="School-year planner"
        description="Teaching and examination timetables appear here when they are generated in Timetable."
        rows={planner.data}
        config={plannerConfig}
        columns={plannerColumns}
        rowKey={(row) => row.id}
        noun={["planner entry", "planner entries"]}
        exportName={{ file: "school-year-planner", title: "School-Year Planner" }}
        isLoading={planner.isLoading}
        isError={planner.isError}
        onRetry={() => planner.refetch()}
        emptyText="Nothing planned yet. Generate a teaching or examination timetable to add it here."
      />

      {viewedYear && (
        <DetailsModal
          title={viewedYear.name}
          headline={{ label: "Academic year", value: `${viewedYear.start_date} → ${viewedYear.end_date}`, note: weeksLabel(viewedYear.start_date, viewedYear.end_date) }}
          status={viewedYear.is_current ? { label: "Current year", dot: "bg-emerald-500" } : { label: "Not current", dot: "bg-gray-400" }}
          facts={[
            ["Starts", viewedYear.start_date],
            ["Ends", viewedYear.end_date],
            ["Terms set up", `${viewedYear.termCount} of 3`],
          ]}
          onClose={() => setViewingYear(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewingYear(null)}>Close</button>
              {canWriteYears && !viewedYear.is_current && (
                <button
                  type="button"
                  disabled={makeCurrentYear.isPending}
                  onClick={() => {
                    if (window.confirm(`Make ${viewedYear.name} the current academic year? The current term is cleared until you mark one of its terms.`)) {
                      makeCurrentYear.mutate(viewedYear.id);
                    }
                  }}
                  className="btn btn-outline gap-1.5"
                >
                  <Star className="h-4 w-4" /> Make current
                </button>
              )}
              {canWriteYears && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => { saveYear.reset(); setEditingYear({ record: viewedYear }); }}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Terms in this year</h4>
              {canWriteTerms && viewedYear.termCount < 3 && (
                <button type="button" onClick={() => { saveTerm.reset(); setEditingTerm({ year: viewedYear.id }); }} className="text-xs font-medium text-primary-700 hover:underline">
                  Add a term
                </button>
              )}
            </div>
            {(() => {
              const own = (terms.data ?? []).filter((term) => term.academic_year === viewedYear.id).sort((a, b) => a.start_date.localeCompare(b.start_date));
              return own.length ? (
                <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {own.map((term) => (
                    <li key={term.id}>
                      <button
                        type="button"
                        onClick={() => { setViewingYear(null); setViewingTerm(term.id); }}
                        className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-left text-sm hover:bg-gray-50"
                      >
                        <span className="font-medium text-gray-900">{termLabel(term)}</span>
                        <span className="text-xs tabular-nums text-gray-500">{term.start_date} → {term.end_date}</span>
                        <StatusDot {...termPhase(term, now)} />
                      </button>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-gray-500">No terms yet.</p>
              );
            })()}
          </section>
        </DetailsModal>
      )}

      {viewedTerm && (
        <DetailsModal
          title={`${termLabel(viewedTerm)} · ${viewedTerm.academic_year_name}`}
          headline={{ label: "Term dates", value: `${viewedTerm.start_date} → ${viewedTerm.end_date}`, note: weeksLabel(viewedTerm.start_date, viewedTerm.end_date) }}
          status={termPhase(viewedTerm, now)}
          facts={[
            ["Academic year", viewedTerm.academic_year_name],
            ["Term", termLabel(viewedTerm)],
            ["Mid-term break starts", viewedTerm.mid_term_break_start || "None"],
            ["Mid-term break ends", viewedTerm.mid_term_break_end || "None"],
          ]}
          notice={
            canDeleteTerms && termInUse && describeUsage(termInUse) ? (
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                In use by {describeUsage(termInUse)}, so it can't be deleted. Correct its dates instead.
              </p>
            ) : confirmingDelete ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                Delete {termLabel(viewedTerm)} of {viewedTerm.academic_year_name}? Nothing uses it yet, and this cannot be undone.
                <div className="mt-2 flex gap-2">
                  <button type="button" className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700" disabled={deleteTerm.isPending} onClick={() => deleteTerm.mutate(viewedTerm.id)}>
                    {deleteTerm.isPending ? "Deleting…" : "Delete term"}
                  </button>
                  <button type="button" className="rounded-md px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100" onClick={() => setConfirmingDelete(false)}>Keep it</button>
                </div>
              </div>
            ) : undefined
          }
          onClose={() => setViewingTerm(null)}
          footer={
            <>
              {termDeletable && !confirmingDelete && (
                <button type="button" onClick={() => setConfirmingDelete(true)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewingTerm(null)}>Close</button>
              {canWriteTerms && !viewedTerm.is_current && (
                <button type="button" disabled={makeCurrentTerm.isPending} onClick={() => makeCurrentTerm.mutate(viewedTerm.id)} className="btn btn-outline gap-1.5">
                  <Star className="h-4 w-4" /> Make current
                </button>
              )}
              {canWriteTerms && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => { saveTerm.reset(); setEditingTerm({ record: viewedTerm }); }}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        />
      )}

      {editingYear && (
        <YearForm
          record={editingYear.record}
          busy={saveYear.isPending}
          error={saveYear.isError ? apiErrorMessage(saveYear.error, "The academic year could not be saved.") : undefined}
          onSave={(data) => saveYear.mutate({ ...data, id: editingYear.record?.id })}
          onClose={() => setEditingYear(null)}
        />
      )}
      {editingTerm && (
        <TermForm
          record={editingTerm.record}
          year={editingTerm.year}
          years={years.data ?? []}
          terms={terms.data ?? []}
          busy={saveTerm.isPending}
          error={saveTerm.isError ? apiErrorMessage(saveTerm.error, "The term could not be saved.") : undefined}
          onSave={(data) => saveTerm.mutate({ ...data, id: editingTerm.record?.id })}
          onClose={() => setEditingTerm(null)}
        />
      )}
    </div>
  );
}

function YearForm({ record, busy, error, onSave, onClose }: {
  record?: AcademicYear;
  busy: boolean;
  error?: string;
  onSave: (data: Omit<AcademicYear, "id">) => void;
  onClose: () => void;
}) {
  const [start, setStart] = useState(record?.start_date ?? "");
  const [end, setEnd] = useState(record?.end_date ?? "");
  const wrongWay = Boolean(start && end && end <= start);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (wrongWay) return;
    const data = new FormData(event.currentTarget);
    onSave({
      name: String(data.get("name")).trim(),
      start_date: start,
      end_date: end,
      is_current: record ? record.is_current : data.get("is_current") === "on",
    });
  };
  return (
    <FormModal
      id="year-form"
      title={record ? `Edit ${record.name}` : "New academic year"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create academic year"}
      note={record ? "Terms must still fall inside the year's dates." : "Add its three terms next."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Academic year" divided={false} columns={2}>
        <Field label="Name" className="sm:col-span-2">
          <input required autoFocus name="name" defaultValue={record?.name} className="input mt-1" placeholder="e.g. 2027 Academic Year" />
        </Field>
        <Field label="Starts">
          <input required type="date" value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends" hint={wrongWay ? <span className="text-red-700">The year must end after it starts.</span> : start && end ? weeksLabel(start, end) : undefined}>
          <input required type="date" min={start || undefined} value={end} onChange={(event) => setEnd(event.target.value)} aria-invalid={wrongWay} className="input mt-1" />
        </Field>
        {!record && (
          <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm sm:col-span-2 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
            <input type="checkbox" name="is_current" className="mt-0.5 h-4 w-4 accent-primary-600" />
            <span>
              <span className="block font-medium text-gray-900">Make this the current academic year</span>
              <span className="block text-xs text-gray-500">The year now current stops being current, and its current term is cleared.</span>
            </span>
          </label>
        )}
      </FormSection>
    </FormModal>
  );
}

function TermForm({ record, year, years, terms, busy, error, onSave, onClose }: {
  record?: AcademicTerm;
  year?: number;
  years: AcademicYear[];
  terms: AcademicTerm[];
  busy: boolean;
  error?: string;
  onSave: (data: Omit<AcademicTerm, "id" | "academic_year_name">) => void;
  onClose: () => void;
}) {
  const defaultYear = record?.academic_year ?? year ?? years.find((item) => item.is_current)?.id ?? years[0]?.id;
  const [yearId, setYearId] = useState(defaultYear ? String(defaultYear) : "");
  const taken = new Set(terms.filter((term) => String(term.academic_year) === yearId && term.id !== record?.id).map((term) => term.term));
  const [which, setWhich] = useState<AcademicTerm["term"] | "">(record?.term ?? TERM_LABELS.find(([value]) => !taken.has(value))?.[0] ?? "");
  const [start, setStart] = useState(record?.start_date ?? "");
  const [end, setEnd] = useState(record?.end_date ?? "");
  const [breakStart, setBreakStart] = useState(record?.mid_term_break_start ?? "");
  const [breakEnd, setBreakEnd] = useState(record?.mid_term_break_end ?? "");
  const chosenYear = years.find((item) => String(item.id) === yearId);
  const problem =
    start && end && end <= start ? "The term must end after it starts."
    : chosenYear && start && end && (start < chosenYear.start_date || end > chosenYear.end_date) ? `Term dates must fall within ${chosenYear.name} (${chosenYear.start_date} to ${chosenYear.end_date}).`
    : Boolean(breakStart) !== Boolean(breakEnd) ? "Give both mid-term break dates, or neither."
    : breakStart && breakEnd && !(start <= breakStart && breakStart < breakEnd && breakEnd <= end) ? "The mid-term break must fall inside the term."
    : "";
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (problem || !which) return;
    const data = new FormData(event.currentTarget);
    onSave({
      academic_year: Number(yearId),
      term: which,
      start_date: start,
      end_date: end,
      mid_term_break_start: breakStart || null,
      mid_term_break_end: breakEnd || null,
      is_current: record ? record.is_current : data.get("is_current") === "on",
    });
  };
  return (
    <FormModal
      id="term-form"
      title={record ? `Edit ${termLabel(record)} · ${record.academic_year_name}` : "New term"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Add term"}
      note="Terms in one year may not overlap."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Term" divided={false} columns={2}>
        <Field label="Academic year" hint={chosenYear ? `${chosenYear.start_date} to ${chosenYear.end_date}` : undefined}>
          <select required value={yearId} onChange={(event) => setYearId(event.target.value)} disabled={Boolean(record)} className="input mt-1">
            <option value="" disabled>Choose a year</option>
            {years.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
          </select>
        </Field>
        <Field label="Term">
          <select required value={which} onChange={(event) => setWhich(event.target.value as AcademicTerm["term"])} className="input mt-1">
            <option value="" disabled>Choose a term</option>
            {TERM_LABELS.map(([value, label]) => (
              <option key={value} value={value} disabled={taken.has(value)}>{label}{taken.has(value) ? " (already added)" : ""}</option>
            ))}
          </select>
        </Field>
        <Field label="Starts">
          <input required type="date" min={chosenYear?.start_date} max={chosenYear?.end_date} value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends" hint={start && end && end > start ? weeksLabel(start, end) : undefined}>
          <input required type="date" min={start || chosenYear?.start_date} max={chosenYear?.end_date} value={end} onChange={(event) => setEnd(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>
      <FormSection title="Mid-term break" description="Optional. Both dates, inside the term." columns={2}>
        <Field label="Starts" optional>
          <input type="date" min={start || undefined} max={end || undefined} value={breakStart} onChange={(event) => setBreakStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends" optional>
          <input type="date" min={breakStart || start || undefined} max={end || undefined} value={breakEnd} onChange={(event) => setBreakEnd(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>
      {problem && <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{problem}</p>}
      {!record && (
        <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
          <input type="checkbox" name="is_current" className="mt-0.5 h-4 w-4 accent-primary-600" />
          <span>
            <span className="block font-medium text-gray-900">This term is in progress now</span>
            <span className="block text-xs text-gray-500">Marks it current in place of any other term.</span>
          </span>
        </label>
      )}
    </FormModal>
  );
}
